import React, { useEffect, useRef, useState } from "react";
import { PRIORITY_OPTIONS, STATUS_OPTIONS } from "./DailyActivityLoggerConstants";
import { todayIsoDate } from "./DailyActivityLoggerUtils";
import { resolveTaskStatusAndProgress } from "./dailyActivityMappers";
import TaskModalShell from "./TaskModalShell";
import {
  MODAL_FIELD,
  MODAL_INPUT,
  MODAL_LABEL,
  MODAL_NOTES,
  MODAL_ROW_2,
  MODAL_SELECT,
  ModalFooterButton,
  ProgressInline,
} from "./taskModalUi";

function TaskFormModal({
  open,
  mode = "create",
  initialTask = null,
  selectedDate,
  context = "today",
  onClose,
  onSubmit,
  saving = false,
}) {
  const [form, setForm] = useState({
    description: "",
    priority: "",
    status: "Pending",
    scheduled_date: selectedDate || todayIsoDate(),
    outcome: "",
    progress_percent: 0,
  });

  const tomorrowIsoDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  };

  const [errors, setErrors] = useState({});
  const initializedRef = useRef(false);
  const isFuturePlan = context === "future";

  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      return;
    }

    if (initializedRef.current) return;
    initializedRef.current = true;

    if (mode === "edit" && initialTask) {
      setForm({
        description: initialTask.description || "",
        priority: initialTask.priority || "",
        status: isFuturePlan ? "Pending" : initialTask.status || "Pending",
        scheduled_date: initialTask.scheduled_date || selectedDate,
        outcome: initialTask.outcome || "",
        progress_percent: isFuturePlan
          ? 0
          : Math.min(100, Math.max(0, Number(initialTask.progress_percent ?? 0))),
      });
    } else {
      setForm({
        description: "",
        priority: "",
        status: "Pending",
        scheduled_date:
          isFuturePlan ? tomorrowIsoDate() : selectedDate || todayIsoDate(),
        outcome: "",
        progress_percent: 0,
      });
    }
    setErrors({});
  }, [open, mode, initialTask, selectedDate, context, isFuturePlan]);

  const setField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "status" && value === "Completed") {
        next.progress_percent = 100;
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.description.trim()) next.description = "Required";
    if (!form.priority) next.priority = "Required";
    if (isFuturePlan && !form.scheduled_date) {
      next.scheduled_date = "Required for future tasks";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const { status, progress_percent } = isFuturePlan
      ? { status: "Pending", progress_percent: 0 }
      : resolveTaskStatusAndProgress(form.status, form.progress_percent);

    onSubmit({
      description: form.description.trim(),
      client_id: null,
      priority: form.priority,
      status,
      scheduled_date: form.scheduled_date,
      outcome: form.outcome.trim(),
      progress_percent,
    });
  };

  if (!open) return null;

  const progress = Number(form.progress_percent) || 0;
  const isEdit = mode === "edit";
  const showStatusAndProgress = !isFuturePlan;

  return (
    <TaskModalShell
      open={open}
      onClose={onClose}
      size="xl"
      eyebrow={isEdit ? "Update task" : "New task"}
      title={isEdit ? "Edit Task" : "Create Task"}
      subtitle={
        isFuturePlan ? "Plan a task for tomorrow" : "Add a task for today"
      }
      footer={
        <>
          <ModalFooterButton onClick={onClose} disabled={saving}>
            Cancel
          </ModalFooterButton>
          <ModalFooterButton
            type="submit"
            form="dal-task-form"
            primary
            disabled={saving}
          >
            {saving ? "Saving..." : isEdit ? "Update Task" : "Create Task"}
          </ModalFooterButton>
        </>
      }
    >
      <form id="dal-task-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className={MODAL_FIELD}>
          <label className={MODAL_LABEL} htmlFor="dal-task-title">
            Task Title <span className="text-red-500">*</span>
          </label>
          <input
            id="dal-task-title"
            type="text"
            value={form.description}
            onChange={(e) => setField("description", e.target.value)}
            placeholder="Describe the task..."
            className={`${MODAL_INPUT} ${
              errors.description ? "border-red-400 ring-1 ring-red-200" : ""
            }`}
            autoFocus
          />
          {errors.description && (
            <p className="mt-1 text-xs text-red-500">{errors.description}</p>
          )}
        </div>

        {showStatusAndProgress ? (
          <div className={MODAL_ROW_2}>
            <div className={MODAL_FIELD}>
              <label className={MODAL_LABEL} htmlFor="dal-task-priority">
                Priority <span className="text-red-500">*</span>
              </label>
              <select
                id="dal-task-priority"
                value={form.priority}
                onChange={(e) => setField("priority", e.target.value)}
                className={`${MODAL_SELECT} ${
                  errors.priority ? "border-red-400 ring-1 ring-red-200" : ""
                }`}
              >
                <option value="">Select priority</option>
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              {errors.priority && (
                <p className="mt-1 text-xs text-red-500">{errors.priority}</p>
              )}
            </div>

            <div className={MODAL_FIELD}>
              <label className={MODAL_LABEL} htmlFor="dal-task-status">
                Status
              </label>
              <select
                id="dal-task-status"
                value={form.status}
                onChange={(e) => setField("status", e.target.value)}
                className={MODAL_SELECT}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div className={MODAL_FIELD}>
            <label className={MODAL_LABEL} htmlFor="dal-task-priority">
              Priority <span className="text-red-500">*</span>
            </label>
            <select
              id="dal-task-priority"
              value={form.priority}
              onChange={(e) => setField("priority", e.target.value)}
              className={`${MODAL_SELECT} ${
                errors.priority ? "border-red-400 ring-1 ring-red-200" : ""
              }`}
            >
              <option value="">Select priority</option>
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            {errors.priority && (
              <p className="mt-1 text-xs text-red-500">{errors.priority}</p>
            )}
          </div>
        )}

        {isEdit && showStatusAndProgress ? (
          <div className={MODAL_FIELD}>
            <span className={MODAL_LABEL}>Progress</span>
            <ProgressInline
              value={progress}
              onChange={(value) => setField("progress_percent", value)}
            />
          </div>
        ) : null}

        <div className={MODAL_FIELD}>
          <label className={MODAL_LABEL} htmlFor="dal-task-notes">
            Notes
          </label>
          <textarea
            id="dal-task-notes"
            value={form.outcome}
            onChange={(e) => setField("outcome", e.target.value)}
            placeholder="Outcome or progress notes..."
            className={`${MODAL_INPUT} ${MODAL_NOTES}`}
          />
        </div>
      </form>
    </TaskModalShell>
  );
}

export default TaskFormModal;
